'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { Title } from '@/components/ui/Headings';

export type CookiePreferences = {
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
};

const COOKIE_CONSENT_NAME = 'revoyager_cookie_consent';
const COOKIE_PREFERENCES_NAME = 'revoyager_cookie_preferences';

export function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const params = useParams();
  const locale = (params?.locale as string) || 'en';

  const [preferences, setPreferences] = useState<CookiePreferences>({
    functional: true, // Always true - required for site to work
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_NAME);
    if (!consent) {
      setShowBanner(true);
    } else {
      // Load saved preferences
      const saved = localStorage.getItem(COOKIE_PREFERENCES_NAME);
      if (saved) {
        try {
          setPreferences(JSON.parse(saved));
        } catch (e) {
          console.error('Failed to parse cookie preferences:', e);
        }
      }
    }
  }, []);

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem(COOKIE_CONSENT_NAME, 'true');
    localStorage.setItem(COOKIE_PREFERENCES_NAME, JSON.stringify(prefs));
    setPreferences(prefs);
    setShowBanner(false);
    setShowSettings(false);

    // Dispatch event for other components to listen to
    window.dispatchEvent(new CustomEvent('cookiePreferencesChanged', { detail: prefs }));
  };

  const acceptAll = () => {
    savePreferences({
      functional: true,
      analytics: true,
      marketing: true,
    });
  };

  const acceptNecessary = () => {
    savePreferences({
      functional: true,
      analytics: false,
      marketing: false,
    });
  };

  const saveCustom = () => {
    savePreferences(preferences);
  };

  const translations = {
    en: {
      title: 'Cookie Preferences',
      description: 'We use cookies to enhance your browsing experience and analyze our traffic. Please choose which cookies you consent to.',
      functional: 'Functional Cookies',
      functionalDesc: 'Required for the website to function properly (authentication, security). Cannot be disabled.',
      analytics: 'Analytics Cookies',
      analyticsDesc: 'Help us understand how visitors interact with our website by collecting anonymous information.',
      marketing: 'Marketing Cookies',
      marketingDesc: 'Used to track visitors across websites to display relevant advertisements.',
      acceptAll: 'Accept All',
      acceptNecessary: 'Accept Necessary Only',
      customize: 'Customize',
      saveSettings: 'Save Settings',
      learnMore: 'Learn more',
    },
    de: {
      title: 'Cookie-Einstellungen',
      description: 'Wir verwenden Cookies, um Ihr Surferlebnis zu verbessern und unseren Traffic zu analysieren. Bitte wählen Sie, welchen Cookies Sie zustimmen.',
      functional: 'Funktionale Cookies',
      functionalDesc: 'Erforderlich für die ordnungsgemäße Funktion der Website (Authentifizierung, Sicherheit). Kann nicht deaktiviert werden.',
      analytics: 'Analyse-Cookies',
      analyticsDesc: 'Helfen uns zu verstehen, wie Besucher mit unserer Website interagieren, indem sie anonyme Informationen sammeln.',
      marketing: 'Marketing-Cookies',
      marketingDesc: 'Werden verwendet, um Besucher über Websites hinweg zu verfolgen und relevante Werbung anzuzeigen.',
      acceptAll: 'Alle akzeptieren',
      acceptNecessary: 'Nur notwendige akzeptieren',
      customize: 'Anpassen',
      saveSettings: 'Einstellungen speichern',
      learnMore: 'Mehr erfahren',
    },
    uk: {
      title: 'Налаштування файлів cookie',
      description: 'Ми використовуємо файли cookie для покращення вашого досвіду перегляду та аналізу нашого трафіку. Будь ласка, виберіть, на які файли cookie ви даєте згоду.',
      functional: 'Функціональні файли cookie',
      functionalDesc: "Необхідні для правильної роботи сайту (аутентифікація, безпека). Не можна вимкнути.",
      analytics: 'Аналітичні файли cookie',
      analyticsDesc: 'Допомагають нам зрозуміти, як відвідувачі взаємодіють з нашим сайтом, збираючи анонімну інформацію.',
      marketing: 'Маркетингові файли cookie',
      marketingDesc: 'Використовуються для відстеження відвідувачів на різних сайтах для показу релевантної реклами.',
      acceptAll: 'Прийняти всі',
      acceptNecessary: 'Прийняти лише необхідні',
      customize: 'Налаштувати',
      saveSettings: 'Зберегти налаштування',
      learnMore: 'Дізнатися більше',
    },
  };

  const t = translations[locale as keyof typeof translations] || translations.en;

  if (!showBanner) {
    return null;
  }

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 px-6 pb-6 z-50">
      <div className="pointer-events-auto max-w-xl rounded-lg bg-white p-6 shadow-[0_20px_60px_0px_rgba(0,0,0,0.3)] border border-dirty-lime-200">
        {!showSettings ? (
          <>
            <p className="text-sm leading-6 text-gray-900">
              {t.description}{' '}
              <Link href={`/${locale}/privacy`} className="font-semibold text-primary-600 hover:text-primary-500">
                {t.learnMore}
              </Link>
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={acceptAll}
                className="px-3 py-2 text-sm bg-dirty-lime-200 text-gray-900 rounded-md hover:bg-dirty-lime-300 transition-colors font-medium shadow ring-1 ring-dirty-lime-200/40"
              >
                {t.acceptAll}
              </button>
              <button
                type="button"
                onClick={acceptNecessary}
                className="px-3 py-2 text-sm bg-white text-gray-900 rounded-md hover:bg-gray-50 transition-colors font-medium border border-gray-200"
              >
                {t.acceptNecessary}
              </button>
              <button
                type="button"
                onClick={() => setShowSettings(true)}
                className="px-3 py-2 text-sm text-gray-700 hover:text-gray-900 transition-colors font-medium rounded-md border border-transparent hover:border-gray-200"
              >
                {t.customize}
              </button>
            </div>
          </>
        ) : (
          <>
            <Title size="sm" className="font-semibold mb-4">{t.title}</Title>
            <div className="space-y-3 mb-4">
              {/* Functional Cookies */}
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <input
                  type="checkbox"
                  id="functional"
                  checked={preferences.functional}
                  disabled
                  className="mt-1 h-4 w-4 text-gray-600 border-gray-300 rounded"
                />
                <label htmlFor="functional" className="flex-1">
                  <span className="text-sm font-medium text-gray-900">{t.functional}</span>
                  <p className="text-xs text-gray-600 mt-0.5">{t.functionalDesc}</p>
                </label>
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <input
                  type="checkbox"
                  id="analytics"
                  checked={preferences.analytics}
                  onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                  className="mt-1 h-4 w-4 text-dirty-lime-600 border-gray-300 rounded focus:ring-dirty-lime-300"
                />
                <label htmlFor="analytics" className="flex-1 cursor-pointer">
                  <span className="text-sm font-medium text-gray-900">{t.analytics}</span>
                  <p className="text-xs text-gray-600 mt-0.5">{t.analyticsDesc}</p>
                </label>
              </div>

              {/* Marketing Cookies */}
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <input
                  type="checkbox"
                  id="marketing"
                  checked={preferences.marketing}
                  onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                  className="mt-1 h-4 w-4 text-dirty-lime-600 border-gray-300 rounded focus:ring-dirty-lime-300"
                />
                <label htmlFor="marketing" className="flex-1 cursor-pointer">
                  <span className="text-sm font-medium text-gray-900">{t.marketing}</span>
                  <p className="text-xs text-gray-600 mt-0.5">{t.marketingDesc}</p>
                </label>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={saveCustom}
                className="px-4 py-2.5 bg-dirty-lime-200 text-gray-900 rounded-lg hover:bg-dirty-lime-300 transition-colors font-medium shadow-sm ring-1 ring-dirty-lime-200/50"
              >
                {t.saveSettings}
              </button>
              <button
                type="button"
                onClick={() => setShowSettings(false)}
                className="px-4 py-2.5 text-gray-900 hover:text-gray-700 transition-colors font-medium"
              >
                Back
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Hook for components to access cookie preferences
export function useCookiePreferences(): CookiePreferences {
  const [preferences, setPreferences] = useState<CookiePreferences>({
    functional: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Load initial preferences
    const saved = localStorage.getItem(COOKIE_PREFERENCES_NAME);
    if (saved) {
      try {
        setPreferences(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse cookie preferences:', e);
      }
    }

    // Listen for changes
    const handleChange = (event: CustomEvent<CookiePreferences>) => {
      setPreferences(event.detail);
    };

    window.addEventListener('cookiePreferencesChanged', handleChange as EventListener);
    return () => {
      window.removeEventListener('cookiePreferencesChanged', handleChange as EventListener);
    };
  }, []);

  return preferences;
}

// Utility function to check if a cookie type is consented
export function hasConsent(cookieType: keyof CookiePreferences): boolean {
  if (typeof window === 'undefined') return false;

  const saved = localStorage.getItem(COOKIE_PREFERENCES_NAME);
  if (!saved) return false;

  try {
    const prefs: CookiePreferences = JSON.parse(saved);
    return prefs[cookieType] === true;
  } catch {
    return false;
  }
}
